/**
 * @license Copyright © HatioLab Inc. All rights reserved.
 */

import './property-editor.js' /* for registering property editors */

import { html, LitElement, PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { CSSStyles, OxPropertyEditor, PropertySpec } from '@operato/property-editor'

/**
모든 에디터들은 change 이벤트를 지원해야 한다. 또한, 모든 에디터들은 value속성에 값을 가져야 한다.

Example:

    <options-builder value="{{value}}">
      <label>Center X</label>
      <input type="number" .value="${value.cx}">
      <label>Width</label>
      <input type="number" .value="${value.width}">
    </options-builder>
*/

const DEFAULT_VALUE = {
  legend: '',
  // number: 0, // should be undefined
  angle: 0,
  string: '',
  password: '',
  textarea: '',
  checkbox: false,
  select: '',
  color: '#000000',
  'solidcolor-stops': null,
  'gradientcolor-stops': null,
  'gltf-selector': '',
  'image-selector': '',
  multiplecolor: null,
  editortable: null,
  imageselector: '',
  options: null,
  date: null
}

function convertValue(type: string, value: any) {
  var converted = String(value).trim() == '' ? undefined : value
  switch (type) {
    case 'number':
    case 'angle':
      converted = parseFloat(value)
      converted = isNaN(converted) ? undefined : converted
      break
  }

  return converted
}

@customElement('function-box-options-builder')
export class FunctionBoxOptionsBuilder extends LitElement {
  @property({ type: Object }) value: { [name: string]: any } = {}
  @property({ type: Array }) props?: PropertySpec[]

  render() {
    return html`<slot></slot>`
  }

  firstUpdated() {
    this.addEventListener('change', this.onValueChanged.bind(this))
  }

  updated(changes: PropertyValues<this>) {
    changes.has('props') && this.onPropsChanged(this.props || [])
    changes.has('value') && this.setValues()
  }

  private onPropsChanged(props: PropertySpec[]) {
    const specs = this.props || []

    this.textContent = ''
    specs.forEach((spec: PropertySpec) => {
      const { label, type, placeholder, name, observe, property, editor, quantifier, defaultValue, disabled, styles } =
        spec

      const elementType = OxPropertyEditor.getEditor(type)
      if (!elementType) {
        console.warn('Property Editor not defined', type)
        return
      }
      const element = document.createElement(elementType) as OxPropertyEditor

      element.label = label
      element.type = type
      element.placeholder = placeholder
      element.name = name
      element.setAttribute('name', name)

      if (observe) {
        element.observeHandler = observe
      }
      element.property = property
      element.editor = editor
      element.quantifier = quantifier
      element.defaultValue = defaultValue
      element.setAttribute('property-editor', 'true')
      element.disabled = disabled

      if (styles) {
        Object.keys(styles).forEach((key: string) => {
          element.style[key as keyof CSSStyles] = styles[key as keyof CSSStyles]
        })
      }

      this.appendChild(element)
    })
  }

  private setValues() {
    this.value &&
      Array.from(this.querySelectorAll('[name]')).forEach((prop: { [name: string]: any }) => {
        let name = prop.getAttribute('name')
        if (!name) {
          return
        }
        var convertedValue = convertValue(prop.type, this.value[name])
        if (convertedValue === undefined) {
          convertedValue = (DEFAULT_VALUE as any)[prop.type]
        }
        prop.value = convertedValue
        prop.observe.call(prop, prop.value)
      })
  }

  private onValueChanged(e: Event) {
    e.stopPropagation()
    var target = e.target as Element

    const prop = target.closest('[property-editor]') as OxPropertyEditor

    var name = prop.getAttribute('name')
    if (!name) {
      return
    }

    this.value[name] = prop.value

    prop.observe(prop.value)
    this.setValues()

    this.dispatchEvent(
      new CustomEvent('property-change', {
        bubbles: true,
        composed: true,
        detail: {
          [name]: prop.value
        }
      })
    )
  }
}
